import axios from 'axios';

const API_BASE_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '') + '/api';

const api = axios.create({ baseURL: API_BASE_URL });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (config.data && !(config.data instanceof FormData) && !config.headers['Content-Type']) {
    config.headers['Content-Type'] = 'application/json';
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !error.config?.skipAuthRedirect) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authService = {
  async register(data) { return (await api.post('/auth/register', data)).data; },
  async login(email, password) {
    const formData = new URLSearchParams();
    formData.append('username', email);
    formData.append('password', password);
    const response = await axios.post(`${API_BASE_URL}/auth/login`, formData, { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } });
    const { access_token, account_type, role } = response.data;
    localStorage.setItem('token', access_token);
    let userData = { email, role, account_type: account_type || 'user' };
    try {
      const userResponse = await api.get('/auth/me', { skipAuthRedirect: true });
      userData = { ...userData, ...userResponse.data, account_type: userResponse.data.account_type || account_type || 'user' };
    } catch (profileError) {
      console.warn('[api.login] profile fetch failed; using login claims', profileError);
    }
    localStorage.setItem('user', JSON.stringify(userData));
    return userData;
  },
  logout() { localStorage.removeItem('token'); localStorage.removeItem('user'); },
  getCurrentUser() { const value = localStorage.getItem('user'); return value ? JSON.parse(value) : null; },
  isAuthenticated() { return !!localStorage.getItem('token'); },
};

export const beachService = {
  async list(city) { return (await api.get('/beaches', { params: city ? { city } : {} })).data; },
  async getNearby(latitude, longitude, maxDistance = 10000) { return (await api.get('/beaches/nearby', { params: { latitude, longitude, max_distance: maxDistance } })).data; },
  async getById(id) { return (await api.get(`/beaches/${id}`)).data; },
};

export const clientService = {
  async list(beachId, isOpen) { const params = {}; if (beachId) params.beach_id = beachId; if (isOpen !== undefined) params.is_open = isOpen; return (await api.get('/kiosks', { params })).data; },
  async getNearby(latitude, longitude, maxDistance = 500) { return (await api.get('/kiosks/nearby', { params: { latitude, longitude, max_distance: maxDistance } })).data; },
  async getById(id) { return (await api.get(`/kiosks/${id}`)).data; },
  async create(data) { return (await api.post('/kiosks', data)).data; },
  async update(id, data) { return (await api.patch(`/kiosks/${id}`, data)).data; },
  async getMyKiosks() { return (await api.get('/kiosks/my/kiosks')).data; },
};
export const kioskService = clientService;

export const productService = {
  async list(clientId, category, isAvailable) { const params = {}; if (clientId) params.client_id = clientId; if (category) params.category = category; if (isAvailable !== undefined) params.is_available = isAvailable; return (await api.get('/products', { params })).data; },
  async getById(id) { return (await api.get(`/products/${id}`)).data; },
  async create(data) { return (await api.post('/products', data)).data; },
  async update(id, data) { return (await api.patch(`/products/${id}`, data)).data; },
  async listPromotional(latitude, longitude) { const params = {}; if (latitude !== undefined && latitude !== null) params.latitude = latitude; if (longitude !== undefined && longitude !== null) params.longitude = longitude; return (await api.get('/products/promotional', { params })).data; },
};

export const orderService = {
  async create(data) { return (await api.post('/orders', data)).data; },
  async list(status) { return (await api.get('/orders', { params: status ? { status } : {} })).data; },
  async getById(id) { return (await api.get(`/orders/${id}`)).data; },
  async getByNumber(orderNumber) { return (await api.get(`/orders/by-number/${orderNumber}`)).data; },
  async updateStatus(id, newStatus) { return (await api.patch(`/orders/${id}/status`, null, { params: { new_status: newStatus } })).data; },
};

export const partnerDashboardService = {
  async orders(params = {}) { return (await api.get('/client-dashboard/orders', { params })).data; },
  async pendingOrders() { return (await api.get('/client-dashboard/orders/pending')).data; },
  async updateOrderStatus(id, status) { return (await api.patch(`/client-dashboard/orders/${id}/status`, { status })).data; },
  async stats() { return (await api.get('/client-dashboard/stats')).data; },
  async notifications(since) { return (await api.get('/client-dashboard/notifications', { params: since ? { since } : {} })).data; },
  async products() { return (await api.get('/client-dashboard/products')).data; },
  async createProduct(data) { return (await api.post('/client-dashboard/products', data)).data; },
  async uploadImage(clientId, file) { const form = new FormData(); form.append('file', file); return (await api.post('/client-dashboard/upload/image', form, { params: { client_id: clientId }, headers: { 'Content-Type': 'multipart/form-data' } })).data; },
  async updateProduct(id, data) { return (await api.patch(`/client-dashboard/products/${id}`, data)).data; },
  async deleteProduct(id) { return api.delete(`/client-dashboard/products/${id}`); },
  async profile() { return (await api.get('/client-dashboard/profile')).data; },
  async updateProfile(data) { return (await api.patch('/client-dashboard/profile', data)).data; },
  async banking(clientId) { return (await api.get('/client-dashboard/banking', { params: clientId ? { client_id: clientId } : {} })).data; },
  async saveBankAccount(data) { return (await api.post('/client-dashboard/banking/account', data)).data; },
  async savePagBankAccount(accountId) { return (await api.post('/client-dashboard/banking/pagbank-account', null, { params: { pagbank_account_id: accountId } })).data; },
  async staff(clientId) { return (await api.get('/client-dashboard/staff', { params: clientId ? { client_id: clientId } : {} })).data; },
  async staffCandidates() { return (await api.get('/client-dashboard/staff/candidates')).data; },
  async createStaffInvitation(data) { return (await api.post('/client-dashboard/staff/invitations', data)).data; },
  async acceptStaffInvitation(token) { return (await api.post(`/client-dashboard/staff/invitations/${token}/accept`)).data; },
  async createStaffMembership(data) { return (await api.post('/client-dashboard/staff/memberships', data)).data; },
  async revokeStaff(userId, clientId) { return (await api.delete(`/client-dashboard/staff/memberships/${userId}/${clientId}`)).data; },
  async reportsSales(params = {}) { return (await api.get('/client-dashboard/reports/sales', { params })).data; },
  async exportSales(params = {}) { const response = await api.get('/client-dashboard/reports/sales/export', { params, responseType: 'blob' }); const url = window.URL.createObjectURL(response.data); const link = document.createElement('a'); link.href = url; link.download = 'relatorio-vendas.csv'; link.click(); window.URL.revokeObjectURL(url); return true; },
  async exportSalesXlsx(params = {}) { const response = await api.get('/client-dashboard/reports/sales/export.xlsx', { params, responseType: 'blob' }); const url = window.URL.createObjectURL(response.data); const link = document.createElement('a'); link.href = url; link.download = 'relatorio-vendas.xlsx'; link.click(); window.URL.revokeObjectURL(url); return true; },
  async exportSalesPdf(params = {}) { const response = await api.get('/client-dashboard/reports/sales/export.pdf', { params, responseType: 'blob' }); const url = window.URL.createObjectURL(response.data); const link = document.createElement('a'); link.href = url; link.download = 'relatorio-vendas.pdf'; link.click(); window.URL.revokeObjectURL(url); return true; },
  async staffShifts(clientId, date) { return (await api.get('/client-dashboard/staff/shifts', { params: { client_id: clientId, ...(date ? { date } : {}) } })).data; },
  async createStaffShift(data) { return (await api.post('/client-dashboard/staff/shifts', data)).data; },
  async clockInShift(id) { return (await api.patch(`/client-dashboard/staff/shifts/${id}/clock-in`)).data; },
  async clockOutShift(id) { return (await api.patch(`/client-dashboard/staff/shifts/${id}/clock-out`)).data; },
  async analytics(params = {}) { return (await api.get('/client-dashboard/analytics/pageviews', { params })).data; },
  async receivables(clientId, status = 'all') { return (await api.get('/client-dashboard/receivables', { params: { client_id: clientId, status } })).data; },
  async status() { return (await api.get('/client-dashboard/status')).data; },
  async toggleOpen(isOpen) { return (await api.patch('/client-dashboard/toggle-open', null, { params: { is_open: isOpen } })).data; },
  async openingHours(clientId) { return (await api.get('/client-dashboard/opening-hours', { params: { client_id: clientId } })).data; },
  async saveOpeningHours(clientId, hours) { return (await api.put('/client-dashboard/opening-hours', hours, { params: { client_id: clientId } })).data; },
  async coupons() { return (await api.get('/coupons/')).data; },
  async createCoupon(data) { return (await api.post('/coupons/', data)).data; },
  async updateCoupon(id, data) { return (await api.patch(`/coupons/${id}`, data)).data; },
  async deleteCoupon(id) { return (await api.delete(`/coupons/${id}`)).data; },
  async stockAlerts(clientId) { return (await api.get('/client-dashboard/stock/alerts', { params: { client_id: clientId } })).data; },
  async stockMovements(clientId, productId) { const params = { client_id: clientId }; if (productId) params.product_id = productId; return (await api.get('/client-dashboard/stock/movements', { params })).data; },
  async createStockMovement(clientId, data) { return (await api.post('/client-dashboard/stock/movement', data, { params: { client_id: clientId } })).data; },
  async status() { return (await api.get('/client-dashboard/status')).data; },
  async toggleOpen(isOpen) { return (await api.patch('/client-dashboard/toggle-open', null, { params: { is_open: isOpen } })).data; },
};

export const paymentService = {
  async createPayment(orderId, paymentMethod, savedCardToken = null) { const params = { order_id: orderId, payment_method: paymentMethod }; if (savedCardToken) params.saved_card_token = savedCardToken; return (await api.post('/payments/create', null, { params })).data; },
  async getByOrder(orderId) { return (await api.get(`/payments/${orderId}`)).data; },
  async mockApprove(orderId) { return (await api.post(`/payments/mock-approve/${orderId}`)).data; },
};

export const cardVaultService = {
  async list() { return (await api.get('/payments/pagbank/cards')).data; },
  async tokenize(cardData) { return (await api.post('/payments/pagbank/cards/tokenize', cardData)).data; },
  async delete(cardToken) { return (await api.delete(`/payments/pagbank/cards/${cardToken}`)).data; },
  async setDefault(cardToken) { return (await api.patch(`/payments/pagbank/cards/${cardToken}/default`)).data; },
};

export default api;
